import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  getIntuneConfig,
  configureIntune,
  updateIntuneConfig,
  deleteIntuneConfig,
  testIntuneConnection,
  getIntuneConsentUrl,
  getIntunePermissions,
  getIntuneStatus,
  disconnectIntune,
  intuneHealthCheck,
} from '../services/api'
import { errorText } from '../utils/errorText'
import { dateTime } from '../utils/date'

interface IntunePermission {
  name: string
  description: string
  /** The feature that needs this permission, or null where the whole integration does. */
  feature?: string | null
  /** Whether the tenant's token carries the role. null when no token could be read — unknown, and
   *  not to be drawn as either held or missing. */
  granted?: boolean | null
}

interface IntuneStatus {
  configured: boolean
  connected: boolean
  method: 'express' | 'advanced' | null
  azure_tenant_id?: string
  azure_tenant_name?: string
  verified_domains?: string[]
  consented_at?: string
  consented_by?: string
  health_status?: string
  last_health_check?: string
  is_connected?: boolean
  last_tested?: string
  last_test_error?: string
  configured_at?: string
  status?: string
  permissions?: IntunePermission[]
  missing_permissions?: string[]
  reconsent_required?: boolean
}

/** Turn the backend's `reason` query param into something a tenant admin can act on.
 *
 * Shared by both paths that surface a consent failure: the full-page redirect and the popup's
 * postMessage relay. The popup path used to ignore the reason entirely and print one generic
 * line, so an already-known specific cause arrived as "Admin consent did not complete. Please
 * try again" and sent the admin round the loop with nothing to go on. */
function consentErrorMessage(reason: string | null | undefined): string {
  const messages: Record<string, string> = {
    consent_denied: 'Admin consent was not granted. Please try again and click Accept.',
    missing_params: 'Invalid callback. Please try the consent flow again.',
    validation_failed: 'Consent validation failed. The link may have expired. Please try again.',
    internal_error: 'The consent may have been recorded even though the server hit an error. '
      + 'Check the connection status below before retrying.',
  }
  if (!reason) return 'Admin consent did not complete. Please try again, or use Advanced Registration.'
  return messages[reason] || `Consent failed: ${reason}`
}

const IntuneIntegration: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [intuneStatus, setIntuneStatus] = useState<IntuneStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showAdvancedForm, setShowAdvancedForm] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)
  const [showUpdateSecret, setShowUpdateSecret] = useState(false)

  // Advanced form state
  const [azureAdTenantId, setAzureAdTenantId] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [authority, setAuthority] = useState('https://login.microsoftonline.com')

  // What the Azure AD consent prompt will ask for. Listed on the page BEFORE the admin clicks
  // Connect, so the request is visible in our own UI and not only in Microsoft's popup. The same
  // list is the set to grant when registering an app manually for Advanced Registration.
  const [requestedPermissions, setRequestedPermissions] = useState<IntunePermission[]>([])

  useEffect(() => {
    // Handle consent callback params
    const intuneParam = searchParams.get('intune')

    // Express consent runs in a POPUP: on completion Azure -> backend redirects the POPUP
    // here with ?intune=connected|error. If we ARE that popup, relay the result to the
    // opener (which refreshes its status and closes us) and stop — otherwise the opener
    // never learns consent finished, so the main window shows no change (the bug).
    if ((intuneParam === 'connected' || intuneParam === 'error') &&
        window.opener && window.opener !== window) {
      try {
        window.opener.postMessage(
          { type: 'intune-consent', result: intuneParam, reason: searchParams.get('reason') || '' },
          window.location.origin,
        )
      } catch { /* opener gone / cross-origin — fall through and render normally */ }
      window.close()
      return
    }

    if (intuneParam === 'connected') {
      setSuccessMessage('Intune connected successfully via Express Registration!')
      searchParams.delete('intune')
      setSearchParams(searchParams, { replace: true })
    } else if (intuneParam === 'error') {
      setError(consentErrorMessage(searchParams.get('reason')))
      searchParams.delete('intune')
      searchParams.delete('reason')
      setSearchParams(searchParams, { replace: true })
    }

    loadStatus()
    getIntunePermissions()
      .then((data) => setRequestedPermissions(data.permissions ?? []))
      .catch(() => { /* the cards fall back to prose without the list */ })
  }, [])

  const loadStatus = async (opts?: { silent?: boolean }) => {
    try {
      // Silent refresh (after save/test/connect): DON'T blank the whole page with the
      // full-screen loader and DON'T clear the just-set success/error banner — otherwise
      // the user gets no visible feedback that their action succeeded.
      if (!opts?.silent) {
        setLoading(true)
        setError(null)
      }
      const status = await getIntuneStatus()
      setIntuneStatus(status)

      // If connected via advanced, load the config for the form
      if (status.method === 'advanced') {
        const config = await getIntuneConfig()
        if (config.configured) {
          setAzureAdTenantId(config.azure_ad_tenant_id || '')
          setClientId(config.client_id || '')
          setAuthority(config.authority || 'https://login.microsoftonline.com')
        }
      }
    } catch (err: any) {
      setError(errorText(err, 'Failed to load Intune status'))
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }

  const handleExpressConnect = async () => {
    setError(null)
    setSuccessMessage(null)

    let consentUrl: string
    try {
      const res = await getIntuneConsentUrl()
      consentUrl = res.consent_url
    } catch (err: any) {
      setError(errorText(err, 'Failed to start Express Registration'))
      return
    }

    const popup = window.open(consentUrl, 'intune-consent', 'width=600,height=700,scrollbars=yes')
    if (!popup) {
      setError('The consent popup was blocked. Please allow popups for this site and try again.')
      return
    }

    // Primary path: the popup postMessages its result back here (see useEffect) and closes.
    // Fallback: if the user closes the popup manually (or postMessage is blocked), refresh
    // once it's gone. Either way the main window now updates without a manual reload.
    let timer: ReturnType<typeof setInterval>
    function finish(result: string, reason?: string) {
      window.removeEventListener('message', onMessage)
      clearInterval(timer)
      if (result === 'connected') {
        setSuccessMessage('Intune connected successfully via Express Registration!')
      } else if (result === 'error') {
        setError(consentErrorMessage(reason))
      }
      try { popup.close() } catch { /* already closed */ }
      loadStatus({ silent: true })
    }
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin || e.data?.type !== 'intune-consent') return
      finish(e.data.result, e.data.reason)
    }
    window.addEventListener('message', onMessage)
    timer = setInterval(() => { if (popup.closed) finish('closed') }, 500)
  }

  const handleAdvancedSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    if (!azureAdTenantId || !clientId || !clientSecret) {
      setError('All fields are required')
      return
    }

    try {
      setSaving(true)
      const configData = {
        azure_ad_tenant_id: azureAdTenantId,
        client_id: clientId,
        client_secret: clientSecret,
        authority: authority,
      }

      const resp = (intuneStatus?.method === 'advanced' && intuneStatus?.configured)
        ? await updateIntuneConfig(configData)
        : await configureIntune(configData)

      setClientSecret('')
      // Reflect the ACTUAL result: the config is saved either way, but only claim success
      // when Azure actually authenticated. Otherwise show why (Azure's real error) so the
      // banner never contradicts the "Not connected" status strip.
      if (resp?.connected) {
        setSuccessMessage('Saved — Intune connected successfully.')
      } else {
        setError(`Saved, but Intune could not authenticate: ${resp?.test_error || 'connection test failed'}`)
      }
      await loadStatus({ silent: true })
    } catch (err: any) {
      setError(errorText(err, 'Failed to save configuration'))
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateSecret = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    if (!clientSecret) {
      setError('Client secret is required')
      return
    }
    // tenant_id + client_id are pre-loaded from the saved config (loadStatus); a rotation
    // only changes the secret, so we reuse them and just send the new secret.
    if (!azureAdTenantId || !clientId) {
      setError('Tenant ID / Client ID not loaded — reload the page and try again')
      return
    }

    try {
      setSaving(true)
      const resp = await updateIntuneConfig({
        azure_ad_tenant_id: azureAdTenantId,
        client_id: clientId,
        client_secret: clientSecret,
        authority: authority,
      })
      setClientSecret('')
      if (resp?.connected) {
        setSuccessMessage('Client secret updated — Intune reconnected.')
        setShowUpdateSecret(false)
      } else {
        // Saved, but Azure rejected it — keep the form open and show the real reason so
        // the admin can paste a corrected value (e.g. a freshly-created secret VALUE).
        setError(`Secret saved, but Intune still can't authenticate: ${resp?.test_error || 'connection test failed'}`)
      }
      await loadStatus({ silent: true })
    } catch (err: any) {
      setError(errorText(err, 'Failed to update client secret'))
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setError(null)
    setSuccessMessage(null)

    try {
      setTesting(true)

      if (intuneStatus?.method === 'express') {
        const result = await intuneHealthCheck()
        if (result.healthy) {
          setSuccessMessage('Express Registration health check passed!')
        } else {
          setError(`Health check failed: ${result.error}`)
        }
      } else {
        const result = await testIntuneConnection()
        if (result.success && result.connected) {
          setSuccessMessage('Connection test successful!')
        } else {
          setError(result.message || 'Connection test failed')
        }
      }
      await loadStatus({ silent: true })
    } catch (err: any) {
      setError(errorText(err, 'Failed to test connection'))
    } finally {
      setTesting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Intune? This will prevent publishing apps to Intune.')) {
      return
    }

    try {
      setSaving(true)
      await disconnectIntune()
      setSuccessMessage('Intune integration disconnected')
      setShowAdvancedForm(false)
      setAzureAdTenantId('')
      setClientId('')
      setClientSecret('')
      await loadStatus({ silent: true })
    } catch (err: any) {
      setError(errorText(err, 'Failed to disconnect'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-theme-text-secondary">Loading...</div>
      </div>
    )
  }

  const isConnected = intuneStatus?.connected

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-theme-text-primary mb-2">Microsoft Intune Integration</h2>
        <p className="text-theme-text-secondary">
          Connect your organization to Microsoft Intune to publish Win32 apps directly.
        </p>
      </div>

      {/* Current-integration status strip — always visible once configured, in EVERY
          state (connected, not-connected, expired), so the user can always see which
          method is active. Previously the method badge only showed inside the connected
          block, so a configured-but-not-connected tenant saw only the fresh selector. */}
      {(intuneStatus?.configured || intuneStatus?.method) && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-theme-secondary border border-theme-border-primary rounded-lg">
          <span className="text-sm text-theme-text-tertiary">Current integration:</span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-400">
            {intuneStatus?.method === 'express' ? 'Express Registration' :
             intuneStatus?.method === 'advanced' ? 'Advanced Registration' : 'Configured'}
          </span>
          <span className="flex items-center gap-1.5 text-sm">
            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span className={isConnected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
              {isConnected ? 'Connected' : 'Not connected'}
            </span>
          </span>
          {!isConnected && intuneStatus?.last_test_error && (
            <span className="text-xs text-red-600 dark:text-red-400">— {intuneStatus.last_test_error}</span>
          )}
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
          <p className="text-green-800 dark:text-green-300">{successMessage}</p>
        </div>
      )}

      {/* Connected State */}
      {isConnected && (
        <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <h3 className="text-lg font-semibold text-theme-text-primary">Connected</h3>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-400">
                {intuneStatus?.method === 'express' ? 'Express' : 'Advanced'}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleTest}
                disabled={testing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                onClick={handleDisconnect}
                disabled={saving}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
              >
                Disconnect
              </button>
            </div>
          </div>

          {/* Connection Details */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {intuneStatus?.azure_tenant_name && (
              <div>
                <span className="text-theme-text-tertiary text-xs">Organization</span>
                <p className="text-theme-text-primary font-medium">
                  {intuneStatus.azure_tenant_name}
                  {intuneStatus.verified_domains && intuneStatus.verified_domains.length > 0 && (
                    <span className="text-theme-text-tertiary font-normal ml-2 text-xs">
                      ({intuneStatus.verified_domains.filter(d => !d.includes('onmicrosoft.com')).join(', ') || intuneStatus.verified_domains[0]})
                    </span>
                  )}
                </p>
              </div>
            )}
            <div>
              <span className="text-theme-text-tertiary text-xs">Azure Tenant ID</span>
              <p className="text-theme-text-primary font-mono text-xs">{intuneStatus?.azure_tenant_id}</p>
            </div>
            {intuneStatus?.consented_by && (
              <div>
                <span className="text-theme-text-tertiary text-xs">Connected By</span>
                <p className="text-theme-text-primary">{intuneStatus.consented_by}</p>
              </div>
            )}
            {intuneStatus?.method === 'express' && intuneStatus?.consented_at && (
              <div>
                <span className="text-theme-text-tertiary text-xs">Connected Since</span>
                <p className="text-theme-text-primary">{new Date(intuneStatus.consented_at).toLocaleDateString()}</p>
              </div>
            )}
            {intuneStatus?.method === 'express' && (
              <div>
                <span className="text-theme-text-tertiary text-xs">Health</span>
                <p className={`font-medium ${
                  intuneStatus.health_status === 'healthy' ? 'text-green-600 dark:text-green-400' :
                  intuneStatus.health_status === 'degraded' ? 'text-yellow-600 dark:text-yellow-400' :
                  'text-red-600 dark:text-red-400'
                }`}>
                  {intuneStatus.health_status === 'healthy' ? 'Healthy' :
                   intuneStatus.health_status === 'degraded' ? 'Degraded' : 'Unknown'}
                </p>
              </div>
            )}
            {intuneStatus?.method === 'advanced' && intuneStatus?.last_tested && (
              <div>
                <span className="text-theme-text-tertiary text-xs">Last Tested</span>
                <p className="text-theme-text-primary">{dateTime(intuneStatus.last_tested)}</p>
              </div>
            )}
          </div>

          {/* Permissions. The dot reflects the tenant's actual grant, read from its Graph token.
              A flat green list here claimed every permission was held, so a tenant that consented
              before one was added to the app registration was told it had one it never got. */}
          {intuneStatus?.method === 'express' && intuneStatus?.permissions && (
            <div className="mt-4 pt-4 border-t border-theme-border-primary">
              <span className="text-theme-text-tertiary text-xs">Permissions</span>

              {intuneStatus.reconsent_required && (
                <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Action needed:</strong> this connection is missing{' '}
                    {intuneStatus.missing_permissions?.length ?? 0} permission
                    {(intuneStatus.missing_permissions?.length ?? 0) === 1 ? '' : 's'}. Permissions
                    added after your administrator approved the connection are not granted until it
                    is approved again.
                  </p>
                  <button
                    onClick={handleExpressConnect}
                    className="mt-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs"
                  >
                    Re-approve permissions
                  </button>
                </div>
              )}

              <div className="mt-2 space-y-1.5">
                {intuneStatus.permissions.map((p) => (
                  <div key={p.name} className="flex items-center gap-2 text-xs">
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        p.granted === true ? 'bg-green-500'
                          : p.granted === false ? 'bg-amber-500'
                            : 'bg-theme-text-tertiary'
                      }`}
                      title={
                        p.granted === true ? 'Granted'
                          : p.granted === false ? 'Not granted'
                            : 'Unknown — could not read the tenant token'
                      }
                    ></span>
                    <span className="font-mono text-theme-text-primary">{p.name}</span>
                    <span className="text-theme-text-tertiary">{p.description}</span>
                    {p.feature && (
                      <span className="text-theme-text-tertiary">— {p.feature} only</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Advanced: self-service client-secret rotation (secret expired / rotating).
              The Advanced secret is customer-owned and expires (Azure secrets last 6-24
              months); without this the only recovery was Disconnect + full reconfigure. */}
          {intuneStatus?.method === 'advanced' && (
            <div className="mt-4 pt-4 border-t border-theme-border-primary">
              {(intuneStatus?.is_connected === false || intuneStatus?.last_test_error) && (
                <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-sm text-red-800 dark:text-red-300">
                    <strong>Action needed:</strong> the Intune connection is failing
                    {intuneStatus?.last_test_error ? ` — ${intuneStatus.last_test_error}` : '.'}
                    {' '}If your Azure AD client secret has expired, create a new one in the
                    Azure Portal and enter it below to restore deployments.
                  </p>
                </div>
              )}
              <button
                onClick={() => setShowUpdateSecret(!showUpdateSecret)}
                className="px-4 py-2 border border-theme-border-primary text-theme-text-primary rounded-lg hover:bg-theme-tertiary text-sm"
              >
                {showUpdateSecret ? 'Cancel' : 'Update Client Secret'}
              </button>
              {showUpdateSecret && (
                <form onSubmit={handleUpdateSecret} className="mt-3 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-theme-text-primary mb-1">
                      New Client Secret *
                    </label>
                    <input
                      type="password"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                      placeholder="Paste the new client secret value from Azure Portal"
                      className="w-full px-3 py-2 border border-theme-border-primary bg-theme-input text-theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-theme-text-tertiary mt-1">
                      Azure Portal &gt; App registrations &gt; your app &gt; Certificates &amp; secrets &gt;
                      New client secret. Tenant ID and Client ID stay the same.
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                  >
                    {saving ? 'Updating...' : 'Update & Reconnect'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Switch method hint */}
          <div className="mt-4 pt-4 border-t border-theme-border-primary">
            <p className="text-xs text-theme-text-tertiary">
              {intuneStatus?.method === 'advanced'
                ? 'Want simpler management? Disconnect and reconnect using Express Registration.'
                : 'Need custom permissions? Disconnect and reconnect using Advanced Registration.'}
            </p>
          </div>
        </div>
      )}

      {/* Registration Mode Selector (when not connected) */}
      {!isConnected && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-theme-text-primary">Choose how to connect:</h3>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Express Registration Card */}
            <div className="bg-theme-secondary border-2 border-blue-500 dark:border-blue-400 rounded-lg p-6 relative">
              <div className="absolute -top-3 left-4">
                <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded">Recommended</span>
              </div>
              <div className="mt-1">
                <h4 className="text-lg font-semibold text-theme-text-primary mb-2">Express Registration</h4>
                <p className="text-sm text-theme-text-secondary mb-4">
                  One-click setup. We handle the app registration and permissions.
                  Your Azure AD admin just approves access in a popup.
                </p>
                <ul className="text-xs text-theme-text-tertiary space-y-1 mb-4">
                  <li>- No Azure Portal setup required</li>
                  <li>- Automatic permission configuration</li>
                  <li>- Centrally managed and always up-to-date</li>
                </ul>
                {requestedPermissions.length > 0 && (
                  <div className="mb-6 pt-3 border-t border-theme-border-primary">
                    <p className="text-xs font-medium text-theme-text-secondary mb-1.5">
                      Your administrator will be asked to grant:
                    </p>
                    <div className="space-y-1">
                      {requestedPermissions.map((p) => (
                        <div key={p.name} className="text-xs text-theme-text-tertiary">
                          <span className="font-mono text-theme-text-secondary">{p.name}</span>
                          {' — '}
                          {p.description}
                          {p.feature && <> ({p.feature} only)</>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  onClick={handleExpressConnect}
                  className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                  Connect to Intune
                </button>
              </div>
            </div>

            {/* Advanced Registration Card */}
            <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
              <h4 className="text-lg font-semibold text-theme-text-primary mb-2">Advanced Registration</h4>
              <p className="text-sm text-theme-text-secondary mb-4">
                Register your own Azure AD app with custom permissions.
                For organizations with strict security policies.
              </p>
              <ul className="text-xs text-theme-text-tertiary space-y-1 mb-6">
                <li>- Full control over app registration</li>
                <li>- Custom permission scoping</li>
                <li>- Sovereign cloud support</li>
              </ul>
              <button
                onClick={() => setShowAdvancedForm(!showAdvancedForm)}
                className="w-full px-4 py-2.5 border border-theme-border-primary text-theme-text-primary rounded-lg hover:bg-theme-tertiary font-medium transition-colors"
              >
                {showAdvancedForm ? 'Hide Manual Setup' : 'Configure Manually'}
              </button>
            </div>
          </div>

          {/* Advanced Registration Form */}
          {showAdvancedForm && (
            <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
              {/* Setup Instructions */}
              <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <button
                  onClick={() => setShowInstructions(!showInstructions)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300">Setup Instructions</h3>
                  <span className="text-blue-600 dark:text-blue-400 text-sm">{showInstructions ? 'Hide' : 'Show'}</span>
                </button>
                {showInstructions && (
                  <div className="mt-3 space-y-2 text-xs text-blue-800 dark:text-blue-300">
                    <p><strong>1.</strong> In Azure Portal, go to Azure AD &gt; App registrations &gt; New registration</p>
                    <p><strong>2.</strong> Select "Accounts in this organizational directory only"</p>
                    <p><strong>3.</strong> Copy the Tenant ID and Client ID from the Overview page</p>
                    <p><strong>4.</strong> Go to Certificates &amp; secrets, create a new client secret</p>
                    <p>
                      <strong>5.</strong> Add Microsoft Graph application permissions:{' '}
                      {requestedPermissions.length > 0
                        ? requestedPermissions
                            .map((p) => (p.feature ? `${p.name} (${p.feature} only)` : p.name))
                            .join(', ')
                        : 'the same permissions Express Registration requests (reload the page if the list did not load)'}
                    </p>
                    <p><strong>6.</strong> Grant admin consent for your organization</p>
                  </div>
                )}
              </div>

              <form onSubmit={handleAdvancedSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-theme-text-primary mb-1">
                    Azure AD Tenant ID *
                  </label>
                  <input
                    type="text"
                    value={azureAdTenantId}
                    onChange={(e) => setAzureAdTenantId(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full px-3 py-2 border border-theme-border-primary bg-theme-input text-theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-text-primary mb-1">
                    Application (Client) ID *
                  </label>
                  <input
                    type="text"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="w-full px-3 py-2 border border-theme-border-primary bg-theme-input text-theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-text-primary mb-1">
                    Client Secret *
                  </label>
                  <input
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="Enter client secret"
                    className="w-full px-3 py-2 border border-theme-border-primary bg-theme-input text-theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-text-primary mb-1">
                    Authority URL
                  </label>
                  <input
                    type="text"
                    value={authority}
                    onChange={(e) => setAuthority(e.target.value)}
                    className="w-full px-3 py-2 border border-theme-border-primary bg-theme-input text-theme-text-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-theme-text-tertiary mt-1">Default: https://login.microsoftonline.com (change for sovereign clouds)</p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save & Connect'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-theme-tertiary border border-theme-border-primary rounded-lg p-4">
        <h3 className="text-sm font-semibold text-theme-text-primary mb-2">About Registration Methods</h3>
        <div className="text-sm text-theme-text-secondary space-y-1">
          <p><strong>Express</strong> uses a shared multi-tenant app managed by Splashtop. Your admin approves permissions via a popup — no Azure Portal setup needed. This is the industry-standard approach for SaaS integrations.</p>
          <p><strong>Advanced</strong> lets you register your own Azure AD app with custom permissions. Choose this if your organization requires full control over third-party app access or uses sovereign cloud environments.</p>
        </div>
      </div>
    </div>
  )
}

export default IntuneIntegration
